import { useContext, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiRequest } from "src/async/apiUtils";
import { WorkspaceContext } from "src/context/workspace";
import { useWorkspaceSwitching } from "src/context/WorkspaceSwitchingContext";
import { getNavigateLink } from "src/helpers/userHelper";
import useUrlQueryParams from "./useUrlQueryParams";
import { setBrowserStyleProperties } from "src/helpers/setBrowserStyleProperties";
import { toast } from "react-toastify";
import { useSiteSettings } from "src/context/SiteSettingsContext";
import useSocketListeners from "./useSocketListeners";

export const useWorkspaceManager = () => {
  const navigate = useNavigate();

  const { site } = useSiteSettings();
  const { workspace_id } = useParams();
  const { startSwitching, finishSwitching } = useWorkspaceSwitching();

  const { group: group_id, page: page_id, document: document_id, form: form_id } = useUrlQueryParams();
  const { details: workspaceDetails, setDetails: setWorkspaceDetails, setLoading: setWorkspaceDetailsLoading, setPlanDetails, setIsPlanDetailsLoaded, setWorspacePaymentModalOpen } = useContext(WorkspaceContext);

  const [loading, setLoading] = useState(false);
  const [currentLogo, setCurrentLogo] = useState("");
  const [switchAdminButtonDisabled, setSwitchAdminButtonDisabled] = useState(false);
  const [settingsSlider, setSettingsSlider] = useState(false);
  const [isContent, setIsContent] = useState(true);

  const clickWorkspace = async (workspace = {}, from = "workspace") => {
    let workspaceId = workspace?._id;

    if (!workspaceId) {
      throw new Error("No workspace available to fetch.");
    }

    if ("" + workspaceId === "" + workspaceDetails?._id && from !== "payments") {
      return;
    }

    if (from !== "payments") {
      startSwitching(workspace, workspaceDetails);
      localStorage.setItem("workspace", workspace?.slug || workspace?._id || "");
      localStorage.setItem("group", "");
      localStorage.setItem("page", "");

      setIsPlanDetailsLoaded(false);
      setWorkspaceDetailsLoading(true);
    }

    const targetWorkspace = workspace?.slug || workspace?._id;

    try {
      const { data: resData } = await apiRequest("GET", `/portal/users/${workspaceId}/page`, {}, { returnErrorWithstatus: true });

      if (resData.status === 200) {
        if (workspaceDetails._id !== workspace._id) {
          setWorkspaceDetails(workspace);
        }
        setIsContent(resData?.is_content);
        const navigate_link = getNavigateLink(resData);
        navigate(navigate_link);
      } else if (["WORKSPACE_PAUSED", "WORKSPACE_PAUSED_SCHEDULED"].includes(resData?.code)) {
        if (workspaceDetails?._id !== workspace?._id) {
          setWorkspaceDetails(workspace);
        }
        finishSwitching();
        navigate(`/workspace/${targetWorkspace}/workspace-paused`, {
          state: {
            message: resData?.message,
            pause_expires_at: resData?.pause_expires_at,
          },
        });
      } else {
        navigate("/permission-denied");
      }
    } catch (err) {
      setIsContent(false);
      const errData = err?.response?.data || err?.data;
      const errStatus = err?.response?.status || err?.status || errData?.status;
      const errCode = errData?.code;

      if (errStatus === 402 || errData?.status === 402) {
        if (workspaceDetails?._id !== workspace?._id) {
          setWorkspaceDetails(workspace);
        }
        navigate(`/workspace/${targetWorkspace}/payment-required`);
      } else if ((errStatus === 403 || errData?.status === 403) && ["WORKSPACE_PAUSED", "WORKSPACE_PAUSED_SCHEDULED"].includes(errCode)) {
        if (workspaceDetails?._id !== workspace?._id) {
          setWorkspaceDetails(workspace);
        }
        finishSwitching();
        navigate(`/workspace/${targetWorkspace}/workspace-paused`, {
          state: {
            message: errData?.message,
            pause_expires_at: errData?.pause_expires_at,
          },
        });
      } else {
        navigate("/permission-denied");
      }
    } finally {
      setWorkspaceDetailsLoading(false);
    }
  };

  const loadPlanDetails = async (workspaceId, refresh = false) => {
    try {
      if (!refresh) {
        setIsPlanDetailsLoaded(false);
        setPlanDetails(null);
      }
      setLoading(true);

      let url = `/portal/workspaces/${workspaceId}/billing/plan`;
      const pageQuery = page_id ? `&page=${page_id}` : document_id ? `&document=${document_id}` : form_id ? `&form=${form_id}` : "";
      url = `${url}?group=${group_id || ""}${pageQuery}`;

      const { data } = await apiRequest("get", url, {}, { returnErrorWithstatus: true });

      setPlanDetails(data?.data);
      setLoading(false);
    } catch (err) {
      setLoading(false);
      setPlanDetails(null);
    } finally {
      setIsPlanDetailsLoaded(true);
      if (!isContent) {
        finishSwitching();
      }
    }
  };

  useSocketListeners([
    {
      event: "workspace-billing-plan-update-response",
      title: "Billing Details Updated",
      message: "The billing details for this workspace has been updated, sync now",
      handler: () => clickWorkspace(workspaceDetails, "payments"),
    },
    {
      event: "workspace-billing-user-update-response",
      title: "Billing Manager Updated",
      message: "The billing manager has beed updated.",
      handler: () => {
        clickWorkspace(workspaceDetails, "payments");
        loadPlanDetails(workspace_id, false);
      },
    },
  ]);

  const navigateToPaymemnts = () => {
    setWorspacePaymentModalOpen(true);
  };

  const navigateToSettings = () => {
    setSettingsSlider(true);
  };

  const extractWorkspaceName = () => {
    const url = window.location.href;
    const pathSegments = url.split("/");

    const workspaceIndex = pathSegments.findIndex((segment) => segment === "workspace");

    if (workspaceIndex !== -1 && workspaceIndex < pathSegments.length - 1) {
      const workspaceName = pathSegments[workspaceIndex + 1];
      return workspaceName;
    }

    return null;
  };

  const loadImage = () => {
    const useWorkspace = workspace_id && !workspaceDetails?.use_global_settings;
    const setting = useWorkspace ? workspaceDetails : site;
    setCurrentLogo(setting?.image_logo || "");
    setBrowserStyleProperties(document, setting);
  };

  const onHandleSwitchToAdmin = async () => {
    try {
      setSwitchAdminButtonDisabled(true);
      const { data } = await apiRequest("get", "/portal/users/self/switch-to-admin");
      window.open(data?.data, "_blank");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSwitchAdminButtonDisabled(false);
    }
  };

  return { loading, currentLogo, switchAdminButtonDisabled, clickWorkspace, loadPlanDetails, navigateToPaymemnts, navigateToSettings, extractWorkspaceName, loadImage, onHandleSwitchToAdmin, settingsSlider, setSettingsSlider };
};
